import {
  CSSProperties,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  Editor,
  throttle,
  TLPageId,
  TLShapeId,
  useEditor,
  useValue,
} from "tldraw"
import { selectedControlColor } from "./Toolbar"
import { useAppDispatch } from "../app/hooks"
import { showPageSettingsDialog } from "../features/app/appSlice"
import { LDPage } from "../../shared/LDPage"
import { CustomDrawShapeProps } from "../../shared/CustomDrawShape"
import { Expr } from "../../shared/Expr"
import { css } from "@mui/material"
import { flattenParams } from "../shapes/flattenParams"
import { LDShape } from "../../shared/LDShape"
import { MarkerShape } from "../../shared/MarkerShape"

const previewImageStyles: CSSProperties = {
  maxHeight: 200,
  width: "100%",
  height: "100%",
  objectFit: "contain",
}

type PagePreviewProps = {
  pageId: TLPageId
}
export function PagePreview({ pageId }: PagePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewDimensions, setPreviewDimensions] = useState<{
    w: number
    h: number
  } | null>(null)
  const editor = useEditor()

  const shapeIds = useValue("shapesIds", () => editor.getPageShapeIds(pageId), [
    editor,
  ])
  const pageMeta = useValue(
    "pageMeta",
    () => {
      const page = editor.getPage(pageId)! as LDPage
      return page.meta
    },
    [editor],
  )

  // collect all the shapes that are not in the process of being drawn
  const completeShapeIds = useValue(
    "shapes",
    () => {
      const _completeShapeIds: TLShapeId[] = []
      for (const id of shapeIds) {
        const s = editor.getShape(id)! as LDShape | MarkerShape

        if (s?.type === "marker-shape") continue

        const params = flattenParams(s, editor)

        console.log({ s })

        if (s && "layer" in s.props) {
          const shapePage = editor.getPage(
            LDShape.getSourcePageId(editor, s),
          )! as LDPage
          const layer = shapePage.meta.layers[LDShape.getLayerId(s)]

          if (Expr.evaluate(layer.visible, params)) {
            if (
              s.type === "custom-draw" &&
              (s.props as CustomDrawShapeProps).isComplete
            ) {
              _completeShapeIds.push(id)
            }
            if (s.type === "sticker") {
              _completeShapeIds.push(id)
            }
            if (s.type === "text2") {
              _completeShapeIds.push(id)
            }
            if (s.type === "custom-line") {
              _completeShapeIds.push(id)
            }
            if (s.type === "custom-geo") {
              _completeShapeIds.push(id)
            }
          }
        }
      }
      return _completeShapeIds
    },
    [editor, shapeIds],
  )

  const blobUrlRef: MutableRefObject<string | null> = useRef(null)

  const updatePreview = useCallback(
    throttle(async (editor: Editor, shapeIds: TLShapeId[]) => {
      // if ids are empty, tldraw exports all the shapes, which is not what we want
      // so we bail if that's the case
      if (shapeIds.length === 0) {
        setPreviewUrl(null)
        return
      }

      const { blob, width, height } = await editor.toImage(shapeIds)

      setPreviewDimensions({ w: width, h: height })

      const blobUrl = URL.createObjectURL(blob)
      setPreviewUrl(blobUrl)
      // clear the previous blob url
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      blobUrlRef.current = blobUrl
    }, 500),
    [],
  )

  useEffect(() => {
    updatePreview(editor, completeShapeIds)
  }, [completeShapeIds, pageMeta, editor])

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
      }}
    >
      {previewUrl && (
        <img
          style={previewImageStyles}
          title={pageId}
          draggable={false}
          src={previewUrl}
        />
      )}
    </div>
  )
}
